// src/components/MapView/SearchBar.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Search, X, MapPin } from "lucide-react";

import {
  useGeographySearch,
  useDisplayConfig,
  useTierConfig,
  useFeatures,
} from "../../hooks";
import { setSelectedGeography, setMapView } from "../../store/mapSlice";

/**
 * SearchBar component - search for geographies by name, ID, etc.
 */
const SearchBar = () => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const { search } = useGeographySearch();
  const { geographyLabels } = useDisplayConfig();
  const { getTierColor } = useTierConfig();
  const { searchEnabled } = useFeatures();

  // Search when query changes
  useEffect(() => {
    if (query.length >= 2) {
      const searchResults = search(query);
      setResults(searchResults);
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query, search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (geography) => {
    const geoId = geography.geoId;
    if (geoId) {
      dispatch(setSelectedGeography(geoId));

      // Center map on selected geography if coordinates available
      if (geography.coordinates) {
        dispatch(
          setMapView({
            center: [geography.coordinates.lat, geography.coordinates.lng],
            zoom: 12,
          }),
        );
      }
    }

    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Don't render if search is disabled in client config
  if (!searchEnabled) return null;

  return (
    <div ref={wrapperRef} className="relative">
      {/* Search input */}
      <div className="relative">
        <Search
          size={18}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={`Search ${geographyLabels.plural.toLowerCase()}...`}
          className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none transition text-sm bg-white"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
          <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500 font-medium">
            {results.length} result{results.length !== 1 ? "s" : ""} found
          </div>
          {results.map((geography, index) => {
            const geoId = geography.geoId;
            const tier = geography.tier;
            const tierColor = getTierColor(tier);

            return (
              <button
                key={geoId || index}
                onClick={() => handleSelect(geography)}
                className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition text-left border-b last:border-b-0"
              >
                <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate text-sm">
                    {geography.city || geography.geoId}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {geography.zcta || geography.geoId}
                    {geography.county &&
                      ` • ${geography.county.replace(" Planning Region", "")}`}
                  </div>
                </div>
                <div className="flex-shrink-0 flex flex-col items-end">
                  <div
                    className="px-2 py-0.5 rounded text-xs font-bold text-white"
                    style={{ backgroundColor: tierColor }}
                  >
                    {geography.score}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    #{geography.rank}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* No results */}
      {isOpen && query.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 text-center">
          <p className="text-sm text-gray-500">
            No {geographyLabels.plural.toLowerCase()} found for {'"'}
            {query}
            {'"'}
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
